// Functions that read a graph's shape: its nodes, edges and `parentId`. This file doesn't import
// `nodes/registry`, so node types can import it without an import cycle.

import { getIncomers, getOutgoers } from '@xyflow/react';
import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import type { ReactFlowEdge, ReactFlowNode } from './types';

/**
 * Gets all ancestor nodes of a given node (nodes that are upstream in the graph).
 *
 * Traverses both:
 * - Edge-based ancestors (nodes with edges pointing to this node)
 * - Subgraph parent chain (via parentId) - if a node is inside a subgraph,
 *   the subgraph parent and its ancestors are included
 *
 * @param nodeId - The ID of the node to find ancestors for
 * @param nodes - All nodes in the graph
 * @param edges - All edges in the graph
 * @returns Array of ancestor nodes in BFS order (closest ancestors first), excludes the starting node
 */
export function getAncestorNodes(
  nodeId: string,
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[]
): ReactFlowNode[] {
  const startNode = nodes.find((n) => n.id === nodeId);
  if (!startNode) {
    return [];
  }

  const ancestors: ReactFlowNode[] = [];
  const visited = new Set<string>([nodeId]);
  const queue: ReactFlowNode[] = [startNode];

  while (queue.length > 0) {
    const current = queue.shift()!;

    // Edge-based ancestors (incomers)
    const parents = getIncomers(current, nodes, edges);
    for (const parent of parents) {
      if (!visited.has(parent.id)) {
        visited.add(parent.id);
        ancestors.push(parent);
        queue.push(parent);
      }
    }

    // Subgraph parent (via parentId)
    if (current.parentId) {
      const subgraphParent = nodes.find((n) => n.id === current.parentId);
      if (subgraphParent && !visited.has(subgraphParent.id)) {
        visited.add(subgraphParent.id);
        ancestors.push(subgraphParent);
        queue.push(subgraphParent);
      }
    }
  }

  return ancestors;
}

/**
 * Gets all descendant nodes of a given node (nodes that are downstream in the graph).
 *
 * Traverses both:
 * - Edge-based descendants (nodes this node has edges pointing to)
 * - Subgraph children (nodes with parentId === this node's id) - if this node
 *   is a SUBGRAPH_PARENT, all nodes inside its subgraph are included
 *
 * @param nodeId - The ID of the node to find descendants for
 * @param nodes - All nodes in the graph
 * @param edges - All edges in the graph
 * @returns Array of descendant nodes in BFS order (closest descendants first), excludes the starting node
 */
export function getDescendantNodes(
  nodeId: string,
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[]
): ReactFlowNode[] {
  const startNode = nodes.find((n) => n.id === nodeId);
  if (!startNode) {
    return [];
  }

  const descendants: ReactFlowNode[] = [];
  const visited = new Set<string>([nodeId]);
  const queue: ReactFlowNode[] = [startNode];

  while (queue.length > 0) {
    const current = queue.shift()!;

    // Edge-based descendants (outgoers)
    const children = getOutgoers(current, nodes, edges);
    for (const child of children) {
      if (!visited.has(child.id)) {
        visited.add(child.id);
        descendants.push(child);
        queue.push(child);
      }
    }

    // Subgraph children (nodes with parentId === current.id)
    const subgraphChildren = nodes.filter((n) => n.parentId === current.id);
    for (const child of subgraphChildren) {
      if (!visited.has(child.id)) {
        visited.add(child.id);
        descendants.push(child);
        queue.push(child);
      }
    }
  }

  return descendants;
}

/**
 * Finds the root node of a subgraph (the node with no incoming edges from other subgraph nodes).
 *
 * @param parentId - The ID of the SUBGRAPH_PARENT node, or undefined for the root graph
 * @param nodes - All nodes in the graph
 * @param edges - All edges in the graph
 * @returns The root node, or null if the subgraph is empty
 */
export function getSubgraphRoot(
  parentId: string | undefined,
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[]
): ReactFlowNode | null {
  for (const node of nodes) {
    if (node.parentId !== parentId) continue;

    // Check if this node has any incoming edge from another node in the same subgraph
    const hasIncomingEdge = edges.some(
      (e) =>
        e.target === node.id &&
        nodes.find((n) => n.id === e.source)?.parentId === parentId
    );

    if (!hasIncomingEdge) return node;
  }

  return null;
}

/**
 * Reports whether a node has at least one outgoing edge (i.e. at least one
 * child in the workflow tree).
 *
 * @param nodeId - The ID of the node to check
 * @param edges - All edges in the graph
 */
export function nodeHasChild(nodeId: string, edges: ReactFlowEdge[]): boolean {
  return edges.some((e) => e.source === nodeId);
}

/** The AI Agent node materialized beneath a Transfer To node's agent fallback. */
export function findTransferEscalationChild(
  transferNodeId: string,
  allNodes: ReactFlowNode[],
  allEdges: ReactFlowEdge[]
): ReactFlowNode | undefined {
  const nodeById = new Map(allNodes.map((n) => [n.id, n]));
  for (const edge of allEdges) {
    if (edge.source !== transferNodeId) continue;
    const child = nodeById.get(edge.target);
    const data = child?.data as LBNodeData | undefined;
    if (data?.ai_call_action?.is_transfer_escalation) {
      return child;
    }
  }
  return undefined;
}
